import * as s from 'assets/styles/Platforms.styles';
import numeral from 'numeral';
import { MAX_EXPORT_ROWS } from 'pages/platform/constants';
import { useEffect } from 'react';

import { Button, Checkbox, Heading, Icon, Message, Spinner, Stack, Text } from '@limepayments/cosmic';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { PaymentExportColumnsObj } from '../../constants';
import { IExportFilterObj } from '../types';

interface Props {
  onCloseExportClick: (val: boolean, closeSection?: boolean) => void;
  exportSaveClickHandler: () => void;
  exportFilterObj: IExportFilterObj;
  setExportColumnsSelectedList: (val: string, isSelectAll?: boolean) => void;
  filterCount: number;
  totalRows: number;
}

function ExportComponent({
  onCloseExportClick,
  exportFilterObj,
  setExportColumnsSelectedList,
  exportSaveClickHandler,
  filterCount,
  totalRows,
}: Props) {
  useEffect(() => {
    if (exportFilterObj.errorMessage.length > 0 || exportFilterObj.exportDataLoader) {
      window.scrollTo({
        top: 250,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [exportFilterObj.errorMessage, exportFilterObj.exportDataLoader]);

  return (
    <ClickAwayListener onClickAway={() => onCloseExportClick(true, true)}>
      <div className="lp-flex lp-flex-column" data-testId="payment-export-dropdown">
        <div className="filter-header">
          <Heading alignment="left" tagName="h2" variant="xs" className="mb-0">
            Export payments
          </Heading>
          <Button
            type="button"
            onClick={() => onCloseExportClick(true, true)}
            size="small"
            variant="ghost"
            className="button-close m-0 p-0"
          >
            <Icon name="Cross" className="m-0 p-0" />
          </Button>
        </div>
        <div className="filter-body">
          {/* listing and error state */}
          {!exportFilterObj.exportDataLoader && !exportFilterObj.exportSuccessfully && (
            <div className="lp-w-full">
              {exportFilterObj.errorMessage.length > 0 && (
                <div className="lp-w-full mb-24">
                  <Message variant="error" children={<span>{exportFilterObj.errorMessage}</span>} type={'inline'} />
                </div>
              )}

              <div className="lp-w-full mb-24">
                <Message
                  children={
                    <Text tagName="p" variant="body-3" isEmphasised>
                      This export has {filterCount || 'no'} applied filters
                    </Text>
                  }
                  type={'inline'}
                  variant={'information'}
                />
              </div>

              {totalRows > MAX_EXPORT_ROWS && (
                <div className="lp-w-full mb-24">
                  <Message dismissalType="none" type="inline" variant="warning">
                    <Heading variant="xxs">Large export</Heading>
                    <Text alignment="left" tagName="p" variant="caption">
                      This export will be limited to {numeral(MAX_EXPORT_ROWS).format()} records. To export all records,
                      please adjust the date filters 'Ordered on' or 'Available on' to a smaller range.
                    </Text>
                  </Message>
                </div>
              )}

              <div className="lp-w-full mb-24">
                <Heading alignment="left" tagName="h2" variant="xxs" className="mb-24">
                  Columns
                </Heading>
                <Checkbox
                  id="testid"
                  label="Select all"
                  variant="body-2"
                  checked={
                    exportFilterObj.exportColumnsSelectedList.length === Object.keys(PaymentExportColumnsObj).length
                  }
                  onChange={(event) => setExportColumnsSelectedList('', true)}
                />
              </div>

              <div className="checkbox-column-list lp-flex">
                <div className="checkbox-columns" data-testId="checkbox-columns">
                  {Object.keys(PaymentExportColumnsObj).map((objKey, key) => (
                    <Checkbox
                      key={key}
                      id={`testid${key}`}
                      label={PaymentExportColumnsObj[objKey as keyof typeof PaymentExportColumnsObj]}
                      variant="body-2"
                      checked={exportFilterObj.exportColumnsSelectedList.includes(objKey)}
                      onChange={() => setExportColumnsSelectedList(objKey)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* loading state */}
          {exportFilterObj.exportDataLoader && !exportFilterObj.exportSuccessfully && (
            <s.MerchantLoading>
              <Stack spacing="md">
                <Spinner isVisible variant="simple" label="Preparing export" />
              </Stack>
            </s.MerchantLoading>
          )}

          {/* success state */}
          {!exportFilterObj.exportDataLoader && exportFilterObj.exportSuccessfully && (
            <s.MerchantLoading>
              <div className="success mb-8">
                <Icon name="Tick" />
              </div>
              <Text alignment="center" tagName="p" variant="body-3" className="max-w320 mb-24">
                Export complete
              </Text>
              <Text alignment="center" tagName="p" variant="body-3" className="max-w320 mt-0">
                <a
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                    exportFilterObj.exportDataCSV ? exportFilterObj.exportDataCSV : '',
                  )}`}
                  download="payments.csv"
                  className="fw-medium lp-link-primary"
                >
                  click here to download
                </a>
              </Text>
            </s.MerchantLoading>
          )}
        </div>
        <div className="filter-footer">
          <Button
            className="lp-w-full"
            onClick={() => onCloseExportClick(true, exportFilterObj.exportSuccessfully ? true : false)}
            size="medium"
            variant="reversed"
            testId={`${exportFilterObj.exportSuccessfully ? 'close' : 'clear'}-button`}
          >
            {`${exportFilterObj.exportSuccessfully ? 'Close' : 'Clear'}`}
          </Button>
          {!exportFilterObj.exportSuccessfully && !exportFilterObj.exportDataLoader && (
            <Button
              className="lp-w-full"
              onClick={() => exportSaveClickHandler()}
              size="medium"
              variant="primary"
              testId="export-button"
            >
              Export
            </Button>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
}

export default ExportComponent;
